/***********************/
/* Angular + external */
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription } from "rxjs";
/***********************/
/* BFX */
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxDialogData } from "./models/bfx-dialog-data.model";
// components
import { BfxDialogBaseComponent } from "./bfx-dialog-base.component";
// services
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxDialogsProcessorService } from "./services/bfx-dialogs-processor.service";

import { LoggerService } from "@bfx/logger";
import {
  BFXNotificationEventType,
  INotificationEvent,
  NotificationService,
  OrderDataActionPromiseService,
} from "shared/cubiq-services";
import { BfxClipboardService } from "shared/helper-services/bfx-clipboard.service";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
const logger = LoggerService.for(__dirname);

export enum EXPORT_OUTPUT_TYPE {
  SERVER_EXPORT,
  CSV_EXPORT,
}

export enum EXPORT_INPUT_TYPE {
  BLOTTER_DATA,
  ARRAY_DATA,
}

export const CRLF = "\r\n";
export const QUOTED_REG_EXP = /^"?(?<unquoted>.*[^"])"?$/;

@Component({
  selector: `bfx-dialog-export`,
  templateUrl: `./html/bfx-dialog-export.html`,
  standalone: false,
})
export class BfxDialogExportComponent extends BfxDialogBaseComponent implements OnInit, OnDestroy {
  /***************************************/
  /* DATA */
  public config: any = {
    debug: false,
  };

  public data = {
    dialogId: null,
    dialogEventSubject: new Subject<BfxDialogData>(),
    actionEvent: null,
    payload: null,
    exportedCSVData: null,
    dialogData: {
      show: false,
      show_mode_exported: false,
      show_mode_clipboard: false,
      export_in_progress: false,
      clipboard_is_supported: true,
      width: 450,
      title_template: "Export blotter as CSV",
      title_text: null,
      button_title_template: "Not supported",
      button_title_text: "",
      message_text_template:
        "Do you want to export data for all selected orders with all visible columns?",
      message_text: null,
      export_output_type: EXPORT_OUTPUT_TYPE.SERVER_EXPORT,
      export_input_type: EXPORT_INPUT_TYPE.BLOTTER_DATA,
      exportMarkup: true,
    },
  };

  private configurationSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private _configService: BfxConfigService,
    private _notificationService: NotificationService,
    private _translationService: TranslateService,
    private _dialogsProcessorService: BfxDialogsProcessorService,
    private _orderActionService: OrderDataActionPromiseService,
    private _clipboardService: BfxClipboardService,
    private chRef: ChangeDetectorRef,
  ) {
    super();
    this.subscribeToConfiguration();
  }

  ngOnDestroy() {
    this.configurationSubscription.unsubscribe();
  }

  ngOnInit() {
    // check if copy to clipboard is supported
    if (!this._clipboardService.isSupported()) {
      this.data.dialogData.clipboard_is_supported = false;
      this.data.dialogData.button_title_text = this.data.dialogData.button_title_template;
    }
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config.debug = config.general.debug;
    });
  }

  /**********************************************************************/
  /* OPEN DIALOG */
  public openDialog(_dialogData: BfxDialogData): Observable<any> {
    try {
      this.data.dialogId = _dialogData.dialogId;
      this.data.actionEvent = _dialogData.event;
      this.data.payload = _dialogData.payload;

      let title_text = this.data.dialogData.title_template;
      let message_text = this.data.dialogData.message_text_template;

      let exportForImport: boolean = this.data.payload.exportForImport;
      if (exportForImport) {
        title_text = "Export Blotter in Import format";
        message_text = "Do you want to export the selected orders to CSV in an importable format?";
      }

      let providedTitle = this.data.payload.title_text;
      if (providedTitle) {
        title_text = providedTitle;
      }

      let providedMessage = this.data.payload.message_text;
      if (providedMessage) {
        message_text = providedMessage;
      }

      NativeDesktopSupport.prepareEmbeddedDialog(_dialogData.body, this._dialog, null);
      this.assignValuesAndShowDialog(title_text, message_text, exportForImport);
    } catch (ex) {
      logger.out.error(
        "ERROR: BfxDialogTradeExportComponent.openDialog(): dialog exception! msg:",
        ex,
      );
    }
    return this.data.dialogEventSubject.asObservable();
  }

  public closeDialog(): void {
    super.closeDialog();

    this.data.dialogData.title_text = null;
    this.data.dialogData.message_text = null;
    this.data.dialogData.button_title_text = "";
    this.data.actionEvent = null;
    this.data.payload = null;
    this.data.exportedCSVData = null;
    this.data.dialogData.show_mode_exported = false;
    this.data.dialogData.show_mode_clipboard = false;
    this.data.dialogData.export_in_progress = false;
    this.data.dialogData.clipboard_is_supported = true;
    this.data.dialogData.show = false;
    // inform dialog service that dialog is closed and to cleanup
    this.notifyDialogClosed(this.data.dialogEventSubject, this.data.dialogId);
  }

  /**********************************************************************/
  /* TRADE EXPORT - Actions */
  public actionExport_csv(): void {
    this.data.dialogData.message_text =
      "Exporting data to CSV format... download will start automatically!";
    this.data.dialogData.show_mode_exported = true;
    this.data.dialogData.export_in_progress = true;

    if (this.data.actionEvent && this.data.actionEvent.selectedIds) {
      logger.out.info(
        this.constructor.name +
          ": Initiating export, export for import " +
          this.data.dialogData.export_output_type +
          " orders: " +
          this.data.actionEvent.selectedIds.length,
      );
    }

    switch (this.data.dialogData.export_output_type) {
      case EXPORT_OUTPUT_TYPE.SERVER_EXPORT: {
        this.doServerExport();
        break;
      }
      case EXPORT_OUTPUT_TYPE.CSV_EXPORT: {
        switch (this.data.dialogData.export_input_type) {
          case EXPORT_INPUT_TYPE.BLOTTER_DATA: {
            this.doCSVBlotterExport();
            break;
          }
          case EXPORT_INPUT_TYPE.ARRAY_DATA: {
            this.doCSVArrayDataExport();
            break;
          }
        }
        break;
      }
    }
  }

  public actionExport_forClipboard(): void {
    this.data.dialogData.message_text = "Exporting data to CSV format... please wait.";
    this.data.dialogData.show_mode_exported = true;
    this.data.dialogData.export_in_progress = true;
    this.data.dialogData.show_mode_clipboard = true;
    this.chRef.detectChanges();

    switch (this.data.dialogData.export_output_type) {
      case EXPORT_OUTPUT_TYPE.SERVER_EXPORT: {
        this.doServerClipBoard();
        break;
      }
      case EXPORT_OUTPUT_TYPE.CSV_EXPORT: {
        switch (this.data.dialogData.export_input_type) {
          case EXPORT_INPUT_TYPE.BLOTTER_DATA: {
            this.doCSVBlotterClipboard();
            break;
          }
          case EXPORT_INPUT_TYPE.ARRAY_DATA: {
            this.doCSVArrayDataClipboard();
            break;
          }
        }
        break;
      }
    }
  }

  doServerExport() {
    this.handleExportForImport().then((value: string) => {
      if (value != null) {
        this.download(value, this.data.payload.blotterName);
      }
    });
  }

  doServerClipBoard() {
    this.handleExportForImport().then((value: string) => {
      if (value != null) {
        this._clipboardService.setContent(value).then(() => {
          if (this._clipboardService.copyContent()) {
            this.closeDialog();
          }
          this.data.dialogData.message_text = "CSV data export completed!";
          this.data.dialogData.export_in_progress = false;
        });
      }
    });
  }

  doCSVBlotterExport() {
    setTimeout(() => {
      this._dialogsProcessorService
        .exportToCSV(this.data.actionEvent, true, this.data.payload.blotterName)
        .then(() => {
          this.data.dialogData.export_in_progress = false;
          this.closeDialog();
        });
    });
  }

  doCSVBlotterClipboard() {
    setTimeout(() => {
      this._dialogsProcessorService
        .exportToCSV(this.data.actionEvent, false, null)
        .then((csvData) => {
          this._clipboardService.setContent(csvData).then(() => {
            this._clipboardService.copyContent();
            this.closeDialog();
          });
        });
    }, 50);
  }

  doCSVArrayDataExport() {
    let headers: string[] = this.data.payload["headers"];
    let rows: string[][] = this.data.payload["rows"];

    let csv: string = this.makeCSV(headers, rows);

    this.download(csv, this.data.payload.blotterName);

    this.data.dialogData.export_in_progress = false;
    this.closeDialog();
  }
  doCSVArrayDataClipboard() {
    let headers: string[] = this.data.payload["headers"];
    let rows: string[][] = this.data.payload["rows"];
    let csv: string = this.makeCSV(headers, rows);

    if (null != csv) {
      this._clipboardService.setContent(csv).then(() => {
        if (this._clipboardService.copyContent()) {
          this.closeDialog();
        }
        this.data.dialogData.message_text = "CSV data export completed!";
        this.data.dialogData.export_in_progress = false;
      });
    } else {
      this.data.dialogData.export_in_progress = false;
      this.closeDialog();
    }
  }

  makeCSV(headers: string[], rows: string[][]): string {
    let ret: string = "";

    let cols = 0;
    headers.forEach((header) => {
      if (0 != cols++) {
        ret = ret + ",";
      }
      ret = ret + this.makeCSVString(header);
    });
    ret = ret + CRLF;

    rows.forEach((row) => {
      cols = 0;
      row.forEach((cell) => {
        if (0 != cols++) {
          ret = ret + ",";
        }
        ret = ret + this.makeCSVString(cell);
      });
      ret = ret + CRLF; // CRLF
    });

    return ret;
  }

  /**
   * escapes header/cell containing " or , in an excel compatible format
   */
  makeCSVString(value: string) {
    let cell = value.match(QUOTED_REG_EXP);

    if (cell != null && typeof cell?.groups?.unquoted === "string") {
      let escape = false;
      value = cell?.groups?.unquoted;

      if (value.includes(`"`)) {
        value = value.replaceAll(`"`, `""`);
        escape = true;
      }

      if (value.includes(`,`)) {
        escape = true;
      }

      if (escape) {
        return `"${value}"`;
      } else {
        return value;
      }
    }

    return value;
  }

  public actionExport_copyToClipboard(): void {
    try {
      this._clipboardService.copyContent();
      this.data.dialogData.message_text += "<br/>Copy to clipboard completed!";
      setTimeout(() => {
        this.closeDialog();
      }, 500);
    } catch (ex) {
      logger.out.error(
        "ERROR: BfxDialogExportComponent.actionExport_copyToClipboard(): Exception: ",
        ex,
      );
      this.closeDialog();
    }
  }

  public actionExport_close(): void {
    this._clipboardService.destroy();
    this.closeDialog();
  }

  /**********************************************************************/
  /* Dialog - Helpers */
  private assignValuesAndShowDialog(
    title_text: string,
    message_text: string,
    exportForImport: boolean = false,
  ): void {
    // assign values
    this.data.dialogData.title_text = title_text;
    this.data.dialogData.message_text = message_text;

    this.data.dialogData.export_input_type = this.data.payload.export_input_type;
    if (null == this.data.dialogData.export_input_type) {
      this.data.dialogData.export_input_type = EXPORT_INPUT_TYPE.BLOTTER_DATA;
    }

    this.data.dialogData.export_output_type = this.data.payload.export_output_type;
    if (null == this.data.dialogData.export_output_type) {
      this.data.dialogData.export_output_type = exportForImport
        ? EXPORT_OUTPUT_TYPE.SERVER_EXPORT
        : EXPORT_OUTPUT_TYPE.CSV_EXPORT;
    }

    // show the dialog
    this.data.dialogData.show = true;
    this.data.dialogData.width = exportForImport ? 550 : 450;
    this.data.dialogData.exportMarkup = exportForImport;
  }

  private handleExportForImport(): Promise<string> {
    return new Promise<string>((resolve) => {
      let orderCount: number = this.data.actionEvent.selectedIds.length;

      this._orderActionService
        .exportOrders(this.data.actionEvent.selectedIds, this.data.dialogData.exportMarkup)
        .then((response: CubiqTS.OrderExportResponseImpl) => {
          logger.out.info(this.constructor.name + ": Received export for import data.");

          if (
            response.errors != null &&
            response.errors instanceof CubiqTS.ErrorsImpl &&
            Array.isArray(response.errors.errorMessages) &&
            response.errors.errorMessages.length > 0
          ) {
            let messages: any[] = response.errors.errorMessages;
            let errors: string[] = messages
              .map((message: any) => JSON.parse(message))
              .map((value: any) => this._translationService.instant(value.id, value.args));
            let time: Date = new Date();
            let noDownload: boolean = response.data == null;
            let notifications: INotificationEvent[] = errors.map((error: string) =>
              this._notificationService.exportErrorToNotification(
                response.messageId,
                error,
                time,
                noDownload,
              ),
            );
            notifications.forEach((notification: INotificationEvent) =>
              this._notificationService.fireNotificationEvent(
                notification,
                BFXNotificationEventType.EXPORT_ERROR_EVENT,
              ),
            );
          }

          if (response.data != null) {
            resolve(response.data);
          }
        })
        .catch((error: CubiqTS.ErrorsImpl) => {
          logger.out.error(
            this.constructor.name +
              ": Error while exporting orders for import " +
              error.errorMessages,
          );

          // Assemble reason
          let reason: string = "";
          if (
            error != null &&
            Array.isArray(error.errorMessages) &&
            error.errorMessages.length > 0
          ) {
            try {
              let msg: any = JSON.parse(error.errorMessages[0].jsonMessage);
              reason = "Reason: " + this._translationService.instant(msg.id, msg.args);
            } catch (e) {
              logger.out.warn(
                this.constructor.name + ": Failed to construct reason from error messsage.",
              );
            }
          }

          logger.out.warn("Export Failed", "Failed to export " + orderCount + " orders. " + reason);
        })
        .finally(() => {
          this.data.dialogData.export_in_progress = false;
          this.closeDialog();
        });
    });
  }

  private download(content: string, _baseFilename: string): void {
    let filename =
      CubiqTS.moment(new Date()).format("YYYY-MM-DD_HH-mm-ss") + "_" + _baseFilename + ".csv";

    const element = document.createElement("a");
    element.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(content));
    element.setAttribute("download", filename);

    element.style.display = "none";
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }
}
